import React, { useEffect, useMemo, useRef } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import { StarterKit } from "@tiptap/starter-kit";
import { Mention } from "@tiptap/extension-mention";
import { VariableDef } from "../variable-input";
import { useCallback, useState } from "react";
import VariableEditPopup from "./variable-edit-popup";
import suggestion, { destroyActiveComponent } from "./suggestion";
import { cn } from "@repo/utils/utils";
import { stripJsonPathRoot } from "@/lib/variable-type-utils";

// removed in favor of extracted components

interface VariableInputEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  variables: Array<VariableDef>;
  multiline?: boolean;
  /** Set to true when inside a Radix Sheet/Dialog */
  inModal?: boolean;
  /** Single-line only: called when Enter is pressed with no active @-suggestion. */
  onSubmit?: () => void;
  /** Borderless, single-row styling for tight inline slots (e.g. the filter pill). */
  compact?: boolean;
  /** Focus the editor once it is created (parity with a native input's autoFocus). */
  autoFocus?: boolean;
  /**
   * Portal target for the mention edit popup. Pass a non-transformed, interactive
   * node (e.g. the surrounding sheet) when this editor sits inside a Radix Popover,
   * whose CSS transform would otherwise break the popup's fixed positioning.
   */
  popupContainer?: Element | DocumentFragment | null;
  /** Pick an array itself instead of drilling into one element (iterator sources). */
  selectArrays?: boolean;
}

// moved VariableEditPopup to its own file

export const VariableInputEditor: React.FC<VariableInputEditorProps> = ({
  value,
  onChange,
  placeholder = "Type @ to insert a variable...",
  className = "",
  variables,
  multiline = true,
  inModal = false,
  onSubmit,
  compact = false,
  autoFocus = false,
  popupContainer,
  selectArrays = false,
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  // Read inside the editor's stable keydown closure without stale captures.
  const onSubmitRef = useRef(onSubmit);
  useEffect(() => {
    onSubmitRef.current = onSubmit;
  }, [onSubmit]);
  const editPopupOpenRef = useRef<boolean>(false);
  const [editPopup, setEditPopup] = useState<{
    isOpen: boolean;
    variable: VariableDef | null;
    position: { x: number; y: number } | null;
    container: Element | DocumentFragment | null;
    /** Document order index of the clicked mention, so identical chips stay distinguishable */
    mentionIndex: number | null;
  }>({
    isOpen: false,
    variable: null,
    position: null,
    container: null,
    mentionIndex: null,
  });

  // Keep ref in sync with state
  useEffect(() => {
    editPopupOpenRef.current = editPopup.isOpen;
  }, [editPopup.isOpen]);

  // Resolve a variable path (e.g., "uuid.result") to display names
  // Returns { shortName: "result", fullPath: "create array > result" }
  // Variables whose names carry pipes (`datetime.now|datetime_format,y`) can't be walked
  // segment by segment, so they are looked up by their full path first.
  const labelsByFullName = useMemo(() => {
    const labels = new Map<string, string[]>();
    const walk = (defs: VariableDef[], namePath: string[], labelPath: string[]) => {
      for (const def of defs) {
        const names = [...namePath, def.name];
        const displayNames = [...labelPath, def.displayName || def.name];
        labels.set(names.join("."), displayNames);
        walk(def.children ?? [], names, displayNames);
      }
    };
    walk(variables, [], []);
    return labels;
  }, [variables]);

  const resolveVariablePath = useCallback(
    (varPath: string): { shortName: string; fullPath: string } => {
      const known = labelsByFullName.get(varPath) ?? labelsByFullName.get(varPath.split("|")[0]);
      if (known) {
        return { shortName: known[known.length - 1], fullPath: known.join(" > ") };
      }
      const parts = stripJsonPathRoot(varPath).split(".");
      const pathNames: string[] = [];
      let currentVars = variables;

      for (const part of parts) {
        const found = currentVars.find((v) => v.name === part);
        if (found) {
          pathNames.push(found.displayName || part);
          currentVars = found.children || [];
        } else {
          // If not found in tree, use the raw part
          pathNames.push(part);
          currentVars = [];
        }
      }

      return {
        shortName: pathNames[pathNames.length - 1] || varPath,
        fullPath: pathNames.join(" > "),
      };
    },
    [variables, labelsByFullName],
  );

  // Convert ${variableName} format to Tiptap mentions
  const convertToTiptapFormat = useCallback(
    (text: string): string => {
      // First escape HTML entities
      let result = text
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");

      // Replace ${variableName} with mention spans
      result = result.replace(/\$\{([^}]+)\}/g, (_, varName) => {
        const { shortName, fullPath } = resolveVariablePath(varName);
        return `<span data-type="mention" data-id="${varName}" data-label="${shortName}" title="${fullPath}">${shortName}</span>`;
      });

      // One paragraph per line. A blank line stays an empty paragraph — filling
      // it with <br> would parse back as a hard break and gain a newline on
      // every round-trip.
      return result
        .split("\n")
        .map((line) => `<p>${line}</p>`)
        .join("");
    },
    [variables, resolveVariablePath],
  );

  // Convert Tiptap mentions back to ${variableName} format
  const convertFromTiptapFormat = useCallback((html: string): string => {
    // Replace mention spans with ${variableName} format
    let result = html.replace(
      /<span[^>]*data-type="mention"[^>]*data-id="([^"]*)"[^>]*>.*?<\/span>/g,
      (_, varName) => `\${${varName}}`,
    );

    // Each paragraph is one line; a <br> is a hard break within one.
    result = result
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/p>\s*<p[^>]*>/g, "\n")
      .replace(/<\/?p[^>]*>/g, "");

    // Remove any remaining HTML tags
    result = result.replace(/<[^>]*>/g, "");

    // Decode HTML entities
    result = result
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&amp;/g, "&")
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&nbsp;/g, " ");

    // Collapse runs of spaces/tabs only — `\s` would eat the newlines the
    // paragraph and <br> conversions above just produced.
    result = result.replace(/[^\S\n]+/g, " ");
    result = result.trim();

    return result;
  }, []);

  // Find a variable by path in the nested variable tree
  const findVariableByPath = useCallback(
    (varPath: string): VariableDef | null => {
      const parts = stripJsonPathRoot(varPath).split(".");
      let currentVars = variables;
      let found: VariableDef | null = null;

      for (const part of parts) {
        found = currentVars.find((v) => v.name === part) || null;
        if (found) {
          currentVars = found.children || [];
        } else {
          return null;
        }
      }

      return found;
    },
    [variables],
  );

  // Handle mention click to open edit popup
  const handleMentionClick = useCallback(
    (event: MouseEvent, mentionElement: HTMLElement, mentionIndex: number) => {
      event.preventDefault();
      event.stopPropagation();

      const variableId = mentionElement.getAttribute("data-id");
      if (!variableId) return;

      // Resolve the display path for showing to user
      const { fullPath } = resolveVariablePath(variableId);

      // Always use the full variableId as the name (this is what gets stored in ${...})
      // The displayName is just for showing a friendly label
      const variable: VariableDef = {
        name: variableId,
        displayName: fullPath || variableId,
      };

      // Always calculate position relative to viewport since we mount to document.body
      const mentionRect = mentionElement.getBoundingClientRect();

      // Calculate position relative to viewport
      const popupWidth = 320;
      const popupHeight = 300;

      let x = mentionRect.left;
      let y = mentionRect.bottom + 5;

      // Adjust horizontal position if popup would go outside viewport
      if (x + popupWidth > window.innerWidth) {
        x = window.innerWidth - popupWidth - 10; // 10px margin from edge
      }
      if (x < 10) {
        x = 10; // 10px margin from left edge
      }

      // Adjust vertical position if popup would go outside viewport
      if (y + popupHeight > window.innerHeight) {
        // Show above the mention instead
        y = mentionRect.top - popupHeight - 5;
        // If still outside viewport, position at bottom with margin
        if (y < 10) {
          y = window.innerHeight - popupHeight - 10;
        }
      }

      // Inline in a transformed ancestor (a centered Radix Dialog), `fixed` is relative to that
      // ancestor, not the viewport.
      let block = inModal && !popupContainer ? mentionElement.parentElement : null;
      while (block && getComputedStyle(block).transform === "none") block = block.parentElement;
      if (block) {
        const rect = block.getBoundingClientRect();
        x = Math.max(rect.left + 10, Math.min(x, rect.right - popupWidth - 10));
        x += block.scrollLeft - rect.left - block.clientLeft;
        y += block.scrollTop - rect.top - block.clientTop;
      }

      const position = { x, y };

      setEditPopup({
        isOpen: true,
        variable,
        position,
        container: popupContainer ?? null,
        mentionIndex,
      });
    },
    [findVariableByPath, resolveVariablePath, popupContainer, inModal],
  );

  // Handle variable update
  const handleVariableUpdate = (oldVariableName: string, newVariableName: string) => {
    if (!editor) return;

    // Resolve the new variable path for display
    const { shortName, fullPath } = resolveVariablePath(newVariableName);

    // Update only the mention that was clicked, not every chip sharing the same path
    const targetIndex = editPopup.mentionIndex;
    const transaction = editor.state.tr;
    let seen = -1;
    let updatedIndex = -1;

    editor.state.doc.descendants((node, pos) => {
      if (node.type.name !== "mention" || updatedIndex !== -1) return;
      seen += 1;

      const isTarget =
        targetIndex !== null && targetIndex >= 0
          ? seen === targetIndex
          : node.attrs.id === oldVariableName;
      if (!isTarget) return;

      transaction.setNodeMarkup(pos, undefined, {
        id: newVariableName,
        label: shortName,
      });
      updatedIndex = seen;
    });

    if (updatedIndex !== -1) {
      editor.view.dispatch(transaction);

      // Update data-tooltip attribute for hover after DOM update
      setTimeout(() => {
        const mentionEl = editor.view.dom.querySelectorAll('span[data-type="mention"]')[
          updatedIndex
        ] as HTMLElement | undefined;
        if (mentionEl) {
          mentionEl.setAttribute("data-tooltip", fullPath);
        }
      }, 0);
    }

    setEditPopup({
      isOpen: false,
      variable: null,
      position: null,
      container: null,
      mentionIndex: null,
    });
  };

  const editor = useEditor(
    {
      extensions: [
        StarterKit.configure({
          heading: false,
          horizontalRule: false,
          bulletList: false,
          orderedList: false,
          listItem: false,
          blockquote: false,
          codeBlock: false,
          code: false,
          bold: false,
          italic: false,
          strike: false,
          paragraph: {},
          hardBreak: multiline ? {} : false,
        }),
        Mention.configure({
          HTMLAttributes: {
            class: "mention",
          },
          // Remove the "@" prefix - just show the label
          renderLabel: ({ node }) => node.attrs.label || node.attrs.id,
          suggestion: {
            ...suggestion,
            char: "@",
            startOfLine: false,
            command: ({ editor, range, props }) => {
              const variable = props as unknown as VariableDef;

              // Extract short name (last part) from full display name path
              const displayNameParts = variable.displayName.split(" > ");
              const shortName = displayNameParts[displayNameParts.length - 1];
              const fullPath = variable.displayName;

              // Delete the range that includes the @ symbol
              editor
                .chain()
                .focus()
                .deleteRange(range)
                .insertContentAt(range.from, [
                  {
                    type: "mention",
                    attrs: {
                      id: variable.name,
                      label: shortName,
                    },
                  },
                ])
                .run();

              // After inserting, find the mention and add data-tooltip attribute for hover
              setTimeout(() => {
                const mentionEl = editor.view.dom.querySelector(
                  `span[data-id="${variable.name}"]`,
                ) as HTMLElement;
                if (mentionEl) {
                  mentionEl.setAttribute("data-tooltip", fullPath);
                }
              }, 0);
            },
          },
        }),
      ],
      content: convertToTiptapFormat(value),
      autofocus: autoFocus ? "end" : false,
      editable: true,
      onUpdate: ({ editor }) => {
        const html = editor.getHTML();
        const convertedValue = convertFromTiptapFormat(html);

        // Apply character limit for single-line mode
        if (!multiline && convertedValue.length > 300) {
          // Prevent the update if it exceeds the limit
          return;
        }

        onChange(convertedValue);
      },
      onBlur: ({ editor }) => {
        // Force close any active suggestion popup when losing focus
        // This is handled by the suggestion system automatically

        // Ensure content is synced when focus is lost
        const html = editor.getHTML();
        const convertedValue = convertFromTiptapFormat(html);
        if (convertedValue !== value) {
          onChange(convertedValue);
        }
      },
      onFocus: () => {
        // Editor focused - let TipTap handle cursor positioning naturally
      },
      editorProps: {
        attributes: {
          class: compact
            ? cn(
                "single-line is-compact hide-scrollbar bg-transparent text-sm focus:outline-none",
                className,
              )
            : cn(
                "prose prose-sm sm:prose-base lg:prose-lg xl:prose-2xl mx-auto focus:outline-none max-w-none px-3 py-2 text-sm border border-input bg-background placeholder:text-muted-foreground field-focus disabled:cursor-not-allowed disabled:opacity-50 rounded-md resize-none",
                {
                  "min-h-[4.5rem] max-h-[8rem] overflow-y-auto": multiline,
                  "h-10 overflow-hidden single-line": !multiline,
                },
                className,
              ),
          "data-placeholder": placeholder,
        },
        handleKeyDown: (view, event) => {
          // Check if there's an active suggestion popup
          const hasActiveSuggestion = document.querySelector(".variable-suggestion-popup");

          // If suggestion dropdown is open, let TipTap handle the keys
          // (Escape is intercepted before it reaches here — see suggestion.tsx)
          if (hasActiveSuggestion) {
            return false;
          }

          // Single-line Enter (no active suggestion) commits instead of inserting a newline.
          if (!multiline && (event.key === "Enter" || event.key === "Return")) {
            onSubmitRef.current?.();
            return true; // Consume the event
          }

          // Check character limit for single-line mode (simple text length check)
          if (!multiline && event.key.length === 1) {
            const currentTextLength = view.state.doc.textContent.length;
            if (currentTextLength >= 300) {
              return true; // Prevent typing if at character limit
            }
          }

          return false;
        },
        handleDOMEvents: {
          wheel: () => {
            // Allow wheel events to bubble up for scrolling when editor is focused
            // This prevents the editor from capturing wheel events and blocking modal scrolling
            return false;
          },
        },
      },
    },
    // `editorProps` (incl. the styling class) is captured at creation, so recreate
    // the editor when the class inputs change — otherwise compact/className updates
    // never take effect.
    [multiline, compact, className],
  );

  // TODO: find out why we use so many useEffects here.

  // Store variables in editor storage for suggestion access
  useEffect(() => {
    if (editor) {
      editor.storage.variables = variables;
      editor.storage.selectArrays = selectArrays;
    }
  }, [editor, variables, selectArrays]);

  // Handle mouse events for React Flow compatibility
  useEffect(() => {
    if (editor) {
      const editorElement = editor.view.dom;

      const handleMouseDown = (event: MouseEvent) => {
        const target = event.target as HTMLElement;

        // Check if clicked element is a mention or contains a mention
        const mentionElement = target.closest('span[data-type="mention"]') as HTMLElement;

        // Note: We don't close the popup here anymore - the document click listener handles it

        if (mentionElement) {
          event.preventDefault();
          event.stopPropagation();
          const mentionIndex = Array.from(
            editorElement.querySelectorAll('span[data-type="mention"]'),
          ).indexOf(mentionElement);
          handleMentionClick(event, mentionElement, mentionIndex);
          return;
        }

        // For normal text clicks, we need to handle focus and cursor positioning
        // because React Flow's elementsSelectable=false blocks normal mouse events
        event.stopPropagation(); // Prevent React Flow from handling this

        // Calculate the position where the user clicked and set cursor there
        const view = editor.view;
        const pos = view.posAtCoords({
          left: event.clientX,
          top: event.clientY,
        });

        if (pos) {
          // Set the cursor position to where the user clicked
          editor.commands.focus();
          editor.commands.setTextSelection(pos.pos);
        } else {
          // Fallback: just focus the editor
          editor.commands.focus();
        }
      };

      const handleClick = (event: MouseEvent) => {
        // Stop propagation to prevent React Flow interference
        event.stopPropagation();
      };

      const handleMouseUp = (event: MouseEvent) => {
        // Stop propagation to prevent React Flow interference
        event.stopPropagation();
      };

      // Tooltip element for mentions
      let tooltipEl: HTMLDivElement | null = null;

      const showTooltip = (mention: HTMLElement) => {
        const tooltipText = mention.getAttribute("data-tooltip");
        if (!tooltipText) return;

        // Create tooltip if it doesn't exist
        if (!tooltipEl) {
          tooltipEl = document.createElement("div");
          tooltipEl.style.cssText = `
            position: fixed;
            padding: 4px 8px;
            background-color: rgb(31 41 55);
            color: white;
            font-size: 0.7rem;
            font-weight: 400;
            border-radius: 4px;
            white-space: nowrap;
            z-index: 10000;
            pointer-events: none;
          `;
          document.body.appendChild(tooltipEl);
        }

        tooltipEl.textContent = tooltipText;
        tooltipEl.style.display = "block";

        // Position tooltip above the mention
        const rect = mention.getBoundingClientRect();
        const tooltipRect = tooltipEl.getBoundingClientRect();
        tooltipEl.style.left = `${rect.left + rect.width / 2 - tooltipRect.width / 2}px`;
        tooltipEl.style.top = `${rect.top - tooltipRect.height - 6}px`;
      };

      const hideTooltip = () => {
        if (tooltipEl) {
          tooltipEl.style.display = "none";
        }
      };

      const handleMouseOver = (event: MouseEvent) => {
        const target = event.target as HTMLElement;
        const mention = target.closest('span[data-type="mention"]') as HTMLElement;
        if (mention) {
          showTooltip(mention);
        }
      };

      const handleMouseOut = (event: MouseEvent) => {
        const target = event.target as HTMLElement;
        const mention = target.closest('span[data-type="mention"]') as HTMLElement;
        if (mention) {
          hideTooltip();
        }
      };

      editorElement.addEventListener("mousedown", handleMouseDown);
      editorElement.addEventListener("click", handleClick);
      editorElement.addEventListener("mouseup", handleMouseUp);
      editorElement.addEventListener("mouseover", handleMouseOver);
      editorElement.addEventListener("mouseout", handleMouseOut);

      return () => {
        editorElement.removeEventListener("mousedown", handleMouseDown);
        editorElement.removeEventListener("click", handleClick);
        editorElement.removeEventListener("mouseup", handleMouseUp);
        editorElement.removeEventListener("mouseover", handleMouseOver);
        editorElement.removeEventListener("mouseout", handleMouseOut);
        // Clean up tooltip element
        if (tooltipEl) {
          tooltipEl.remove();
        }
      };
    }
  }, [editor, handleMentionClick, variables]);

  // Update editor content when value prop changes (but not when user is actively editing)
  useEffect(() => {
    if (editor && !editor.isFocused) {
      const currentValue = convertFromTiptapFormat(editor.getHTML());
      if (value !== currentValue) {
        const convertedContent = convertToTiptapFormat(value);
        editor.commands.setContent(convertedContent);
      }
    }
  }, [value, editor, convertFromTiptapFormat, convertToTiptapFormat]);

  // Add data-tooltip attributes to mentions for hover tooltips
  // This runs after content is loaded since TipTap doesn't preserve custom attributes from HTML
  useEffect(() => {
    if (editor) {
      // Small delay to ensure DOM is updated
      const timeoutId = setTimeout(() => {
        const mentions = editor.view.dom.querySelectorAll('span[data-type="mention"]');
        mentions.forEach((mention) => {
          const varId = mention.getAttribute("data-id");
          if (varId) {
            const { fullPath } = resolveVariablePath(varId);
            mention.setAttribute("data-tooltip", fullPath);
          }
        });
      }, 10);
      return () => clearTimeout(timeoutId);
    }
  }, [editor, value, resolveVariablePath]);

  // Add blur event listener to close mention popup when editor loses focus
  useEffect(() => {
    if (editor) {
      const editorElement = editor.view.dom;

      const handleBlur = () => {
        // Small delay to allow for interactions with the popup
        setTimeout(() => {
          // Check if focus is in the editor or the suggestion popup
          const popup = document.querySelector(".variable-suggestion-popup");
          const focusInEditor = editorElement.contains(document.activeElement);
          const focusInPopup = popup?.contains(document.activeElement);

          if (!focusInEditor && !focusInPopup) {
            // Close the active suggestion popup if it exists
            destroyActiveComponent();
          }
        }, 100);
      };

      editorElement.addEventListener("blur", handleBlur);

      return () => {
        editorElement.removeEventListener("blur", handleBlur);
      };
    }
  }, [editor]);

  if (!editor) {
    return (
      <div
        className={`max-h-[8rem] min-h-[4.5rem] rounded-md border border-input bg-background px-3 py-2 text-sm text-gray-400 field-focus ${className}`}
      >
        {placeholder}
      </div>
    );
  }

  return (
    <div
      ref={editorRef}
      className="relative w-full cursor-text"
      onMouseDown={(event) => {
        // Don't handle mousedown if the edit popup is open
        if (editPopupOpenRef.current) {
          return;
        }
        // Ensure editor gets focus when clicking anywhere in the container
        if (editor && !editor.isFocused) {
          event.stopPropagation();

          // Calculate position and set cursor
          const view = editor.view;
          const pos = view.posAtCoords({
            left: event.clientX,
            top: event.clientY,
          });

          if (pos) {
            editor.commands.focus();
            editor.commands.setTextSelection(pos.pos);
          } else {
            editor.commands.focus();
          }
        }
      }}
    >
      <EditorContent
        editor={editor}
        className={cn("variable-input-editor", compact && "compact-editor")}
        placeholder={placeholder}
      />
      {!value && (
        <div
          // Lighter than `text-muted-foreground`: the editor body inherits
          // Typography's `prose` gray-700, which that token sits too close to.
          className={cn("pointer-events-none absolute truncate text-sm text-gray-400", {
            "left-[17px] top-[18px]": multiline,
            "left-3 top-1/2 -translate-y-1/2": !multiline && !compact,
            "left-1 top-1/2 -translate-y-1/2": compact,
          })}
        >
          {placeholder}
        </div>
      )}

      {/* Variable Edit Popup */}
      <VariableEditPopup
        isOpen={editPopup.isOpen}
        onClose={() =>
          setEditPopup({
            isOpen: false,
            variable: null,
            position: null,
            container: null,
            mentionIndex: null,
          })
        }
        variable={editPopup.variable}
        onVariableUpdate={handleVariableUpdate}
        position={editPopup.position}
        container={editPopup.container}
        usePortal={!inModal}
      />

      <style jsx global>{`
        .variable-input-editor .ProseMirror {
          outline: none;
          white-space: pre-wrap;
          word-wrap: break-word;
          cursor: text;
          user-select: text;
          -webkit-user-select: text;
          -moz-user-select: text;
          -ms-user-select: text;
        }
        .variable-input-editor .ProseMirror p {
          margin: 0;
          line-height: 1.5;
        }
        .variable-input-editor .ProseMirror.single-line {
          height: 2.5rem;
          white-space: nowrap;
          overflow-x: auto;
          overflow-y: hidden;
          display: flex;
          align-items: center;
          padding-left: 0.75rem;
          padding-right: 0.75rem;
          position: relative;
          user-select: text;
          -webkit-user-select: text;
          -moz-user-select: text;
          -ms-user-select: text;
        }
        .variable-input-editor .ProseMirror.single-line p {
          margin: 0;
          white-space: nowrap;
          line-height: 1.5rem;
          flex: 1;
        }
        /* Compact, borderless variant for tight inline slots (e.g. the filter pill).
           Scoped to the reactive wrapper class and forced with !important so it
           wins over whatever styling the ProseMirror froze in at editor creation. */
        .variable-input-editor.compact-editor .ProseMirror {
          border: none !important;
          box-shadow: none !important;
          background: transparent !important;
          padding: 0 0.25rem !important;
          height: 1.5rem !important;
          min-height: 0 !important;
          font-size: 0.875rem !important;
          line-height: 1.5rem !important;
        }
        .variable-input-editor .ProseMirror:empty:before,
        .variable-input-editor .ProseMirror p:empty:before {
          display: none !important;
        }
        .variable-input-editor .ProseMirror.single-line:empty:before,
        .variable-input-editor .ProseMirror.single-line p:empty:before {
          display: none !important;
        }
        .variable-input-editor .ProseMirror p:only-child:empty:before {
          display: none !important;
        }
        .variable-input-editor .ProseMirror .mention {
          display: inline-block !important;
          vertical-align: middle;
          border-radius: 0.375rem;
          background-color: rgb(219 234 254);
          padding: 0.125rem 0.375rem;
          font-size: 0.75rem;
          font-weight: 500;
          color: rgb(30 64 175);
          cursor: pointer;
          margin: 0 0.125rem;
          transition: all 0.2s;
          border: 1px solid rgb(191 219 254);
          line-height: 1.2;
        }
        .variable-input-editor .ProseMirror .mention:hover {
          background-color: rgb(191 219 254);
          border-color: rgb(147 197 253);
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }
      `}</style>
    </div>
  );
};
